import { useMutation, useQueryClient } from "@tanstack/react-query";
import services from "../services";
import { ORDER } from "../enums";
import useNotification from "../../../utils/useNotification";
import { PartConfirmationBucketEntry } from "../../../components/SupplierInventorySearch/utils/partsCalculationUtils";

export type PartConfirmationSendStatus = "sent" | "failed";

export interface PartConfirmationSendResult {
  supplierId: string;
  supplierName: string;
  status: PartConfirmationSendStatus;
  error?: string;
}

interface SendPartConfirmationBucketInput {
  orderId: string;
  orderNo: string;
  entries: PartConfirmationBucketEntry[];
}

const extractErrorMessage = (error: unknown): string => {
  const anyError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    anyError?.response?.data?.message ||
    anyError?.message ||
    "Failed to send part confirmation"
  );
};

/**
 * Sends a part-confirmation request to every supplier in the Part Confirmation Bucket in
 * one action. Each supplier is an isolated `POST /v1/part-confirmations` — one failure
 * never blocks the rest (`Promise.allSettled`). The payload per supplier is identical to
 * the single-supplier `RequestPartConfirmationModal` flow.
 */
export const useSendPartConfirmationBucket = () => {
  const queryClient = useQueryClient();
  const notify = useNotification();

  return useMutation({
    mutationFn: async ({
      orderId,
      orderNo,
      entries,
    }: SendPartConfirmationBucketInput): Promise<
      PartConfirmationSendResult[]
    > => {
      const settled = await Promise.allSettled(
        entries.map((entry) =>
          services.createPartConfirmation({
            orderId,
            orderNo,
            supplierId: entry.supplierId,
            parts: entry.confirmationParts,
          }),
        ),
      );

      return settled.map((outcome, index) => {
        const entry = entries[index];
        if (outcome.status === "fulfilled") {
          return {
            supplierId: entry.supplierId,
            supplierName: entry.supplierName,
            status: "sent",
          };
        }
        return {
          supplierId: entry.supplierId,
          supplierName: entry.supplierName,
          status: "failed",
          error: extractErrorMessage(outcome.reason),
        };
      });
    },
    onSuccess: (results, variables) => {
      const sentCount = results.filter((r) => r.status === "sent").length;
      const failedCount = results.length - sentCount;

      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATION_RESPONSES],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATIONS_FOR_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.SUPPLIER_INVENTORY_DELTAS],
      });

      if (failedCount === 0) {
        notify({
          status: "success",
          title: "Part confirmation sent",
          description: `Sent to ${sentCount} supplier${
            sentCount === 1 ? "" : "s"
          } for enquiry ${variables.orderNo}`,
          duration: 4000,
        });
      } else {
        notify({
          status: sentCount > 0 ? "warning" : "error",
          title: `${sentCount} sent, ${failedCount} failed`,
          description: results
            .filter((r) => r.status === "failed")
            .map((r) => `${r.supplierName}: ${r.error}`)
            .join(" | "),
          duration: 6000,
        });
      }
    },
    onError: (error) => {
      notify({
        status: "error",
        title: "Failed to send part confirmation",
        description: extractErrorMessage(error),
      });
    },
  });
};
